/**
 * Strip HTML tags and decode entities for safe plain-text display.
 * Use when surfacing product descriptions or other content that may contain raw HTML.
 */

// Named entities that turn up in vendor copy. Numeric references (`&#8217;`,
// `&#x2019;`) are decoded generically below, so this table only needs names.
const NAMED_ENTITIES: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
  rsquo: '\u2019',
  lsquo: '\u2018',
  rdquo: '\u201d',
  ldquo: '\u201c',
  sbquo: '\u201a',
  bdquo: '\u201e',
  hellip: '\u2026',
  mdash: '\u2014',
  ndash: '\u2013',
  bull: '\u2022',
  middot: '\u00b7',
  deg: '\u00b0',
  times: '\u00d7',
  divide: '\u00f7',
  plusmn: '\u00b1',
  minus: '\u2212',
  le: '\u2264',
  ge: '\u2265',
  trade: '\u2122',
  reg: '\u00ae',
  copy: '\u00a9',
  frac12: '\u00bd',
  frac14: '\u00bc',
  frac34: '\u00be',
  micro: '\u00b5',
  ordm: '\u00ba',
  oslash: '\u00f8',
  Oslash: '\u00d8',
  eacute: '\u00e9',
  egrave: '\u00e8',
  agrave: '\u00e0',
  ccedil: '\u00e7',
  uuml: '\u00fc',
  ouml: '\u00f6',
  auml: '\u00e4',
  Phi: '\u03a6',
  alpha: '\u03b1',
  euro: '\u20ac',
  pound: '\u00a3',
  cent: '\u00a2',
};

/** Decode HTML entities (named, decimal, hex). Unknown names are left as written. */
export function decodeHtmlEntities(text: string | undefined | null): string {
  if (text == null || typeof text !== 'string') return '';
  return text.replace(/&(#x?[0-9a-fA-F]+|[a-zA-Z][a-zA-Z0-9]{1,31});/g, (match, body: string) => {
    if (body[0] === '#') {
      const hex = body[1] === 'x' || body[1] === 'X';
      const code = Number.parseInt(hex ? body.slice(2) : body.slice(1), hex ? 16 : 10);
      if (!Number.isFinite(code) || code <= 0 || code > 0x10ffff) return match;
      try {
        return String.fromCodePoint(code);
      } catch {
        return match;
      }
    }
    const named = NAMED_ENTITIES[body] ?? NAMED_ENTITIES[body.toLowerCase()];
    return named === undefined ? match : named;
  });
}

export function stripHtml(html: string | undefined | null): string {
  if (html == null || typeof html !== 'string') return '';
  return decodeHtmlEntities(html.replace(/<[^>]*>/g, ' '))
    .replace(/\s+/g, ' ')
    .trim();
}
